import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import {
  DeletionInFlightRun,
  DeletionPlan,
  useDeleteIntegrationMutation,
  useGetIntegrationDeletionPreviewQuery,
} from "features/integrationsBeta/integrationsBeta";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useState } from "react";
import { formatApiError } from "utils/formatApiError";

/** Structural so a future list-page row action can pass a monitor row. */
export interface DeletableIntegration {
  id: number;
  name: string;
  client_name: string;
  is_active: boolean;
}

interface Props {
  open: boolean;
  integration: DeletableIntegration;
  onClose: () => void;
  /** Called after the delete succeeded; the page clears its guard + navigates. */
  onDeleted: () => void;
}

/** Long lists are capped so the report stays readable without a scroll pane. */
const MAX_LIST = 5;

function plural(count: number, one: string, many?: string): string {
  return `${count} ${count === 1 ? one : (many ?? `${one}s`)}`;
}

function MicroHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="caption"
      component="h3"
      sx={{
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontWeight: 700,
        color: "text.secondary",
        display: "block",
        mt: 2,
        mb: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function BulletList({ items }: { items: React.ReactNode[] }) {
  const shown = items.slice(0, MAX_LIST);
  const hidden = items.length - shown.length;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
      {shown.map((item, index) => (
        // Index keys are fine: the list is rebuilt whole on every render and
        // has no interactive children.
        <Typography key={index} variant="body2" component="li">
          {item}
        </Typography>
      ))}
      {hidden > 0 && (
        <Typography
          variant="body2"
          component="li"
          sx={{ color: "text.secondary" }}
        >
          +{hidden} more
        </Typography>
      )}
    </Box>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{
        fontFamily: "monospace",
        fontSize: "0.8rem",
        wordBreak: "break-all",
      }}
    >
      {children}
    </Box>
  );
}

function removalItems(plan: DeletionPlan): React.ReactNode[] {
  const { removals } = plan;
  const items: React.ReactNode[] = [];

  if (removals.steps.count > 0) {
    items.push(
      <>
        {plural(removals.steps.count, "pipeline step")}:{" "}
        {(removals.steps.names ?? []).join(", ")}
      </>,
    );
  }
  (removals.schedules ?? []).forEach((schedule) => {
    items.push(
      <>
        Schedule &quot;{schedule.display_name}&quot; ({schedule.cron_expression}
        )
        {schedule.aws_managed ? (
          <>
            {" - also deleted in AWS EventBridge Scheduler as "}
            <Mono>{schedule.aws_schedule_name}</Mono>
          </>
        ) : (
          " - DB row only, no AWS resource behind it"
        )}
      </>,
    );
  });
  (removals.webhook_triggers ?? []).forEach((trigger) => {
    items.push(
      <>
        Webhook trigger &quot;{trigger.name}&quot; ({trigger.provider})
        {trigger.connection_will_be_deleted
          ? ` + its dedicated connection "${trigger.connection_name}" and webhook secret`
          : ` - its connection "${trigger.connection_name}" is kept (still in use)`}
      </>,
    );
  });
  if (removals.runs.count > 0) {
    items.push(
      <>
        {plural(removals.runs.count, "run")} and{" "}
        {plural(removals.step_executions.count, "step execution")} - the run
        history is not recoverable
      </>,
    );
  }
  if (removals.processed_sftp_files.count > 0) {
    items.push(
      <>
        {plural(removals.processed_sftp_files.count, "SFTP ledger row")} -
        previously delivered files become eligible again if the integration is
        recreated
      </>,
    );
  }
  const folder = removals.storage_folder;
  if (folder && folder.will_be_removed) {
    items.push(
      <>
        Data-Storage folder &quot;{folder.display_name}&quot; with{" "}
        {plural(folder.file_rows, "file record")} (
        <Mono>
          s3://{folder.s3_bucket}/{folder.folder_prefix}
        </Mono>
        )
      </>,
    );
  }
  if (items.length === 0) {
    items.push(<>Nothing else is attached to this integration.</>);
  }
  return items;
}

function retentionItems(plan: DeletionPlan): React.ReactNode[] {
  const { retentions } = plan;
  const items: React.ReactNode[] = [];

  items.push(
    <>
      All archived files in S3 (
      <Mono>
        s3://{retentions.s3_objects.s3_bucket}/{retentions.s3_objects.prefix}
      </Mono>
      ) - only the records listing them are removed
    </>,
  );
  (retentions.connections ?? []).forEach((connection) => {
    items.push(
      <>
        Connection &quot;{connection.name}&quot; ({connection.type} &middot;{" "}
        {connection.provider}) with its credentials
        {connection.used_by_other_integrations > 0
          ? ` - used by ${plural(
              connection.used_by_other_integrations,
              "other integration",
            )}`
          : ""}
      </>,
    );
  });
  if (retentions.config_profile) {
    items.push(
      <>
        Shared config profile &quot;{retentions.config_profile.name}&quot; (used
        by {plural(retentions.config_profile.used_by, "integration")})
      </>,
    );
  }
  if (retentions.processed_webhook_events.count > 0) {
    items.push(
      <>
        {plural(retentions.processed_webhook_events.count, "webhook audit row")}{" "}
        (kept, no longer linked to an integration)
      </>,
    );
  }
  return items;
}

function BlockerRun({ run }: { run: DeletionInFlightRun }) {
  const hours = Math.floor(run.age_minutes / 60);
  const age =
    hours > 0 ? `${hours}h ${run.age_minutes % 60}m` : `${run.age_minutes}m`;
  return (
    <Typography variant="body2" component="li">
      {run.status} for {age} &middot; <Mono>{run.execution_id}</Mono>
      {run.is_stale && " - likely dead, safe to force"}
      {run.worker_id && (
        <>
          <br />
          task: <Mono>{run.worker_id}</Mono>
        </>
      )}
    </Typography>
  );
}

export default function DeleteIntegrationDialog({
  open,
  integration,
  onClose,
  onDeleted,
}: Props) {
  const dispatch = useAppDispatch();
  const [confirmText, setConfirmText] = useState("");
  const [force, setForce] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  /** Set when the DELETE itself answered 409 (a run started after the preview). */
  const [serverBlocked, setServerBlocked] = useState(false);

  const {
    data: plan,
    isFetching,
    isError,
    refetch,
  } = useGetIntegrationDeletionPreviewQuery(integration.id, { skip: !open });
  const [deleteIntegration, { isLoading: deleting }] =
    useDeleteIntegrationMutation();

  const blockers = plan?.blockers ?? [];
  const isBlocked = blockers.length > 0 || serverBlocked;
  const nameMatches = confirmText.trim() === integration.name.trim();
  const canDelete =
    !!plan && !isFetching && !deleting && nameMatches && (!isBlocked || force);

  // A freshly opened dialog never inherits a previous attempt's state.
  useEffect(() => {
    if (open) {
      setConfirmText("");
      setForce(false);
      setErrorText(null);
      setServerBlocked(false);
    }
  }, [open]);

  // Force must never linger once there is nothing to override.
  useEffect(() => {
    if (!isBlocked) setForce(false);
  }, [isBlocked]);

  const handleDelete = async () => {
    setErrorText(null);
    try {
      await deleteIntegration({
        id: integration.id,
        force: force || undefined,
      }).unwrap();
      dispatch(
        pushNotification({
          severity: "success",
          message: `Integration "${integration.name}" deleted.`,
        }),
      );
      onDeleted();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setErrorText(
        formatApiError(
          (err as { data?: unknown })?.data,
          "Could not delete the integration.",
        ),
      );
      if (status === 409) {
        // A run started between the preview and the DELETE - reveal force and
        // pull a fresh blocker list.
        setServerBlocked(true);
        refetch();
      }
    }
  };

  return (
    <AppDialog
      open={open}
      // Escape and the backdrop are inert while the teardown is running.
      onClose={deleting ? () => {} : onClose}
      title={`Delete "${integration.name}"`}
      maxWidth="sm"
      showCloseIcon={!deleting}
      actions={
        <>
          <Button
            onClick={onClose}
            disabled={deleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={!canDelete}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {deleting ? "Deleting..." : force ? "Force delete" : "Delete"}
          </Button>
        </>
      }
    >
      {errorText && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorText}
        </Alert>
      )}

      <Typography
        variant="subtitle2"
        sx={{
          color: "error.main",
          fontWeight: 800,
          textTransform: "uppercase",
        }}
      >
        Warning
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        Deleting <strong>{integration.name}</strong> ({integration.client_name})
        cannot be undone.
      </Typography>
      {integration.is_active && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          This integration is currently enabled.
        </Alert>
      )}

      {isFetching && !plan && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            justifyContent: "center",
            py: 4,
          }}
        >
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            Checking what will be deleted...
          </Typography>
        </Box>
      )}

      {isError && !plan && (
        <Alert
          severity="error"
          sx={{ mt: 2 }}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        >
          Could not load the deletion impact report. Deleting is disabled until
          it loads.
        </Alert>
      )}

      {plan && (
        <>
          <MicroHeading>Will be removed</MicroHeading>
          <BulletList items={removalItems(plan)} />

          <Divider sx={{ mt: 2 }} />

          <MicroHeading>Will be kept</MicroHeading>
          <BulletList items={retentionItems(plan)} />

          {(plan.warnings ?? []).length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {plan.warnings.map((warning) => (
                  <Typography key={warning.code} variant="body2" component="li">
                    {warning.message}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}
        </>
      )}

      {isBlocked && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Blocked by in-flight runs</AlertTitle>
          {blockers.length > 0 ? (
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {blockers.flatMap((blocker) =>
                blocker.runs.map((run) => (
                  <BlockerRun key={run.execution_id} run={run} />
                )),
              )}
            </Box>
          ) : (
            <Typography variant="body2">
              A run started after the report was generated.
            </Typography>
          )}
          <Button
            size="small"
            sx={{ mt: 1, textTransform: "none" }}
            disabled={isFetching || deleting}
            onClick={() => {
              setServerBlocked(false);
              refetch();
            }}
          >
            Re-check
          </Button>
        </Alert>
      )}

      <Collapse in={isBlocked}>
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Checkbox
              color="error"
              checked={force}
              disabled={deleting}
              onChange={(_, checked) => setForce(checked)}
            />
          }
          label="Force delete despite in-flight runs"
        />
        {force && (
          <Alert severity="error" sx={{ mb: 1 }}>
            Forcing does not stop the running task - the platform cannot stop an
            ECS task. It keeps executing while its run record is deleted, and it
            may already have delivered data to the destination system. Stop the
            task in the ECS console first.
          </Alert>
        )}
      </Collapse>

      {plan && (
        <TextField
          sx={{ mt: 2 }}
          label={`Type "${integration.name}" to confirm`}
          placeholder={integration.name}
          value={confirmText}
          onChange={(event) => setConfirmText(event.target.value)}
          error={confirmText.length > 0 && !nameMatches}
          helperText={
            confirmText.length > 0 && !nameMatches
              ? "Name does not match."
              : " "
          }
          size="small"
          fullWidth
          autoComplete="off"
          disabled={deleting}
        />
      )}
    </AppDialog>
  );
}
